import PropTypes from 'prop-types';
import SearchQuery from '@App/api/query/Search.graphql';
import Query from '@App/components/api/Query';
import ResultBox from '@App/components/search/ResultBox';
import ResultTabs from '@App/components/search/ResultTabs';
import AnalyticsPlatformTag from '@App/searchAnalytics/AnalyticsPlatformTag';
import AnalyticsTag from '@App/searchAnalytics/AnalyticsTag';

interface Props {
  onSearch: () => void
  onFind: () => void
  onSubmit: () => void
  mobile: boolean
  limit: number
  minChars: number
  includedTags: string[]
  excludedTags: string[]
  search: string | null
}

function mapResults(data) {
  const { searchTags, searchTracks, searchAlbums, searchPlaylists } = data;

  return {
    loading: false,
    tags: {
      count: searchTags.totalCount,
      list: searchTags.edges.map(edge => edge.node.tag),
    },
    tracks: {
      count: searchTracks.totalCount,
      list: searchTracks.edges.map(edge => {
        const { track, highlightResult } = edge.node;
        const matchedVersions = (highlightResult.versions || [])
          .filter(version => version.fullyHighlighted)
          .map(version => version.value);

        return Object.assign({ matchedVersions }, track);
      }),
    },
    albums: {
      count: searchAlbums.totalCount,
      list: searchAlbums.edges.map(edge => edge.node.album),
    },
    playlists: {
      count: searchPlaylists.totalCount,
      list: searchPlaylists.edges.map(edge => edge.node.playlist),
    },
  };
}

/**
 * Global search results: the music ones, from Algolia, and nothing else.
 *
 * The sound effects used to be searched here too, through a separate parallel query to the PSE
 * proxy, and shown as a fifth column. The client asked for them out: searching the music library is
 * not the moment to be offered sound effects, and the four original columns are what that menu is
 * for. So no PSE call is made from here at all — not merely a hidden result, an unmade request to a
 * billed, rate-limited partner API.
 *
 * The result components still know how to render the section: {@see ResultBox} and
 * {@see ResultTabs} take `soundEffects`/`onSoundEffects` as optional props and show nothing without
 * them. Passing them again is all it would take to bring the column back, the day the client asks.
 * The sound-effects search itself is untouched on /effets-sonores, which has its own search bar.
 */
export default function Search({
  onSearch,
  onFind,
  onSubmit,
  mobile,
  limit,
  minChars,
  includedTags,
  excludedTags,
  search,
}: Props) {
  const disabled = search === null || search.length < minChars;

  if (disabled) {
    return null;
  }

  const Result = mobile ? ResultTabs : ResultBox;
  const analyticsTags = [AnalyticsPlatformTag.from(mobile), AnalyticsTag.SEARCHBAR];

  return (
    <Query
      component={Result}
      loadingComponent={Result}
      query={SearchQuery}
      variables={{ search, includedTags, excludedTags, limit, analyticsTags }}
      childProps={{ mobile, onSearch, onFind, onSubmit }}
      mapResult={mapResults}
    />
  );
}

Search.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onFind: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mobile: PropTypes.bool.isRequired,
  limit: PropTypes.number.isRequired,
  minChars: PropTypes.number.isRequired,
  includedTags: PropTypes.arrayOf(
    PropTypes.string.isRequired,
  ).isRequired,
  excludedTags: PropTypes.arrayOf(
    PropTypes.string.isRequired,
  ).isRequired,
  search: PropTypes.string,
};

Search.defaultProps = {
  search: null,
};
